import { InlineEditor, TextInput } from '@my/ui';
import { Plus, X } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ORDER_REF_LABELS_MAX_ROWS,
  ORDER_REF_LABELS_MIN_ROWS,
} from '@/utils/orderReferenceLabels';

// Re-export so the OrganisationPage's barrel-style import keeps
// working with a single statement — round-trip helpers + the component
// itself all flow through this module to the page.
export {
  ORDER_REF_LABELS_MIN_ROWS,
  ORDER_REF_LABELS_MAX_ROWS,
  parseOrderReferenceLabels,
  serialiseOrderReferenceLabels,
} from '@/utils/orderReferenceLabels';

export interface OrderReferenceLabelsEditorProps {
  /**
   * Labels in slot order. Index `i` is the label for `Reference${i+1}`.
   * Must always carry at least {@link ORDER_REF_LABELS_MIN_ROWS}
   * entries — the parent's hydration helper guarantees that.
   */
  value: string[];
  /**
   * Replaces the whole array. The parent owns serialisation to the
   * BE-bound JSON shape — see {@link serialiseOrderReferenceLabels}.
   */
  onChange: (next: string[]) => void;
}

/**
 * Configures the per-org names of `Order.Reference1..N` slots on the
 * TMS Order Details page. Stored as a JSON object on
 * `Organisation.TmsOrderReferenceLabelsJson` keyed by slot name
 * (`Reference1`, `Reference2`, …).
 *
 * <p>
 * Mirrors the {@link SequenceConfigEditor} pattern: an
 * <c>InlineEditor</c> summary line with a panel underneath. The
 * panel's footer X / ✓ buttons only collapse the panel — the actual
 * save fires from the parent form's green-tick alongside the rest of
 * the Organisation page's changes.
 * </p>
 *
 * <p>
 * Always surfaces at least {@link ORDER_REF_LABELS_MIN_ROWS} rows; the
 * "+ Add reference" affordance grows the list up to
 * {@link ORDER_REF_LABELS_MAX_ROWS}. The per-row X is hidden when at
 * the minimum so the operator can't accidentally drop below two slots.
 * </p>
 */
export function OrderReferenceLabelsEditor({
  value,
  onChange,
}: OrderReferenceLabelsEditorProps) {
  const { t } = useTranslation('organisation');

  // Summary line shows whatever the operator typed, slot-by-slot,
  // joined with the middle dot — same separator the SequenceConfigEditor
  // uses for its preview row so the two summary lines look consistent.
  // Empty slots render as the fallback label (`Reference 1`, …) so the
  // line is never just a wall of dots.
  const summary = useMemo(() => {
    return value
      .map((v, i) => v.trim() || t('orderReferenceLabels.fallbackSlotLabel', { n: i + 1 }))
      .join(' · ');
  }, [value, t]);

  const handleSlotChange = (index: number, next: string) => {
    const out = value.slice();
    out[index] = next;
    onChange(out);
  };

  const handleRemove = (index: number) => {
    if (value.length <= ORDER_REF_LABELS_MIN_ROWS) return;
    onChange(value.filter((_, i) => i !== index));
  };

  const handleAdd = () => {
    if (value.length >= ORDER_REF_LABELS_MAX_ROWS) return;
    onChange([...value, '']);
  };

  return (
    <InlineEditor label={t('orderReferenceLabels.title')} summary={summary}>
      <div className="space-y-3">
        <p className="text-xs italic text-slate-500">{t('orderReferenceLabels.helper')}</p>
        <div className="space-y-2">
          {value.map((label, idx) => (
            <div key={idx} className="flex items-start gap-2">
              <div className="flex-1">
                <TextInput
                  label={t('orderReferenceLabels.slotLabel', { n: idx + 1 })}
                  name={`order-reference-label-${idx + 1}`}
                  autoComplete="off"
                  value={label}
                  maxLength={64}
                  placeholder={t('orderReferenceLabels.slotPlaceholder', { n: idx + 1 })}
                  onChange={(e) => handleSlotChange(idx, e.target.value)}
                />
              </div>
              {value.length > ORDER_REF_LABELS_MIN_ROWS && (
                <button
                  type="button"
                  aria-label={t('orderReferenceLabels.removeRow', { n: idx + 1 })}
                  onClick={() => handleRemove(idx)}
                  className="mt-1 flex h-7 w-7 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-200"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
        {value.length < ORDER_REF_LABELS_MAX_ROWS && (
          <button
            type="button"
            aria-label={t('orderReferenceLabels.ariaAddReference')}
            onClick={handleAdd}
            className="flex items-center gap-1 self-start rounded-sm px-1.5 py-0.5 text-xs font-medium text-purple-600 hover:bg-purple-50"
          >
            <Plus className="h-3.5 w-3.5" />
            {t('orderReferenceLabels.addReference')}
          </button>
        )}
      </div>
    </InlineEditor>
  );
}
